/**
 * Purpose: Type definitions for core game state, players, turns, and configuration.
 * Owns: Player profile, game state machine, turn tracking, class progression,
 *       game mode, and schedule types.
 * Notes: Redesigned for the "School Day" classroom progression system.
 *        Players progress through 8 class periods + a Final Exam (period 9).
 *        Each class is a grid of seats; correct answers let you roll dice and
 *        advance seats. Single-device model for Phase 1.
 */

import type { AgeGroup, Category, Question, Tier } from './question';
import type { ClassSize } from '../config/classConfig';

/**
 * Academic major — used by High School and College age groups.
 * Younger groups and Parents default to 'General Ed'.
 */
export type Major =
  | 'General Ed'
  | 'Math'
  | 'Science'
  | 'History'
  | 'English'
  | 'Art'
  | 'Geography'
  | 'Music'
  | 'Sports';

/** Colors available for player tokens on the board. */
export const PLAYER_COLORS = [
  '#e74c3c',
  '#3498db',
  '#2ecc71',
  '#f39c12',
  '#9b59b6',
  '#1abc9c',
  '#e67e22',
  '#e84393',
] as const;

export type PlayerColor = (typeof PLAYER_COLORS)[number];

// _______________________________________________
//              CLASSROOM PROGRESSION TYPES
// _______________________________________________
// These types model the "school day" structure where each player
// moves through class periods independently, sitting in desks/seats.

/** A single class period in a player's schedule. */
export interface ClassPeriod {
  /** The school subject taught in this class */
  subject: Category | 'Final Exam';
  /** Seat layout and count for this class */
  classSize: ClassSize;
  /** Which period number this is (1-9, where 9 = Final Exam) */
  periodNumber: number;
}

/** Tracks a player's progress within a single class period. */
export interface PlayerClassProgress {
  /** Current seat position (0 = not yet seated, 1+ = seated) */
  currentSeat: number;
  /** Total questions answered in this class */
  questionsAnswered: number;
  /** Questions answered correctly in this class */
  questionsCorrect: number;
  /** Questions answered correctly but with a hint (subset of questionsCorrect) */
  questionsWithHelp: number;
  /** Flat percentage docked from the final grade for behavior (starts at 0) */
  behaviorDock: number;
  /** Letter grade earned (null until class is passed) */
  letterGrade: string | null;
  /** Whether the player has passed (graduated from) this class */
  passed: boolean;
}

/** Game mode determines the win condition and gameplay style. */
export type GameMode = 'speedRun' | 'honorRoll' | 'pureClassroom';

/** Schedule mode determines how class order is assigned. */
export type ScheduleMode = 'random' | 'choose';

// _______________________________________________
//              PLAYER TYPE
// _______________________________________________

/** A single player in the game session. */
export interface Player {
  /** Unique player ID (generated at creation) */
  id: string;
  /** Player display name */
  name: string;
  /** Which age group determines their question pool */
  ageGroup: AgeGroup;
  /** Difficulty tier within their age group */
  tier: Tier;
  /** Academic major — only meaningful for High School / College age groups */
  major: Major;
  /** Token color on the board and scoreboard */
  color: PlayerColor;
  /** Cumulative score (dice points earned from correct answers) */
  score: number;
  /** Current period the player is attending (1-9) */
  currentPeriod: number;
  /** The player's class schedule (ordered list of periods) */
  schedule: ClassPeriod[];
  /** Progress tracking for each class period (indexed by period number - 1) */
  classProgress: PlayerClassProgress[];
  /** Cumulative grade point average across all passed classes */
  gpa: number;
  /** Whether the player has finished all classes including Final Exam */
  finished: boolean;
  /** Total questions answered correctly this game */
  questionsCorrect: number;
  /** Total questions answered incorrectly this game */
  questionsWrong: number;
  /** Current consecutive correct-answer streak */
  currentStreak: number;
  /** Best streak achieved in this game */
  bestStreak: number;
}

/** Which phase of the game loop we're currently in. */
export type GamePhase =
  | 'setup'
  | 'rolling'
  | 'question'
  | 'answer-reveal'
  | 'board-view'
  | 'results';

/** Snapshot of a single turn's events. */
export interface Turn {
  /** Which player is taking this turn */
  playerId: string;
  /** The dice roll result for this turn (1-6) */
  diceValue: number | null;
  /** The question presented this turn */
  question: Question | null;
  /** Index of the answer the player selected (null if unanswered) */
  selectedAnswer: number | null;
  /** Whether the player answered correctly */
  wasCorrect: boolean | null;
  /** Points earned this turn (0 if wrong, diceValue if correct) */
  pointsEarned: number;
}

/** Settings chosen during game setup that don't change mid-game. */
export interface GameConfig {
  /** ID of the question pack being used */
  packId: string;
}

/** The full game state — single source of truth for the entire session. */
export interface GameState {
  /** All players in this session, in turn order */
  players: Player[];
  /** Index into the players array for whose turn it is */
  currentPlayerIndex: number;
  /** Which round we're on (increments after all players take a turn) */
  currentRound: number;
  /** Data for the current turn in progress */
  currentTurn: Turn;
  /** Current dice roll value (mirrors currentTurn.diceValue for quick access) */
  diceValue: number | null;
  /** Current phase of the game loop */
  gamePhase: GamePhase;
  /** The question currently being displayed (null if not in question phase) */
  currentQuestion: Question | null;
  /** Game configuration set during setup */
  config: GameConfig;
  /** IDs of questions already used this session (prevents repeats) */
  usedQuestionIds: Set<string>;
  /** Host reaction message to display (from AI host system) */
  hostReaction: string | null;
  /** Whether this is a speed run or honor roll game */
  gameMode: GameMode;
  /** Whether schedules are randomized or player-chosen */
  scheduleMode: ScheduleMode;
}
